import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { DashboardData } from '@fleet/shared';
import { api } from '../../lib/apiClient';

/** Fetch the dashboard for a period token ('YYYY-MM' | 'YTD' | 'ALL'), or latest. */
export function useDashboard(period?: string | null) {
  return useQuery({
    queryKey: ['dashboard', period ?? 'latest'],
    queryFn: () =>
      api.get<DashboardData>(`/dashboard${period ? `?period=${encodeURIComponent(period)}` : ''}`),
    // Keep the current dashboard on screen while a new period loads, so the page
    // doesn't blank out and scroll position is preserved.
    placeholderData: keepPreviousData,
  });
}

/** Waar de Excel-uitdraai van één periode vandaan komt: `ALL`, `YTD` of `JJJJ-MM`. */
export function buitenlandXlsxUrl(periode: string): string {
  return `/api/dashboard/buitenland/${encodeURIComponent(periode)}/xlsx`;
}

/**
 * Vastleggen wat een twijfelachtige tankbeurt werkelijk was.
 *
 * Een lijst regels en niet één plaats: het actiecentrum bundelt de vraag per plaats,
 * maar het antwoord hangt aan de transacties die op dát moment in die bundel zaten.
 * Een tankbeurt op dezelfde naam op de factuur van volgende maand valt erbuiten en
 * wordt gewoon opnieuw gevraagd.
 *
 * Invalideert elke dashboardperiode, niet alleen die op het scherm: de pagina houdt
 * er drie tegelijk vast (TCO, per persoon, tankgedrag), en in een stale kopie zou de
 * vraag nog openstaan.
 */
export function useBuitenlandOordeel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      costLineIds,
      oordeel,
    }: {
      costLineIds: string[];
      oordeel: 'NEDERLAND' | 'BUITENLAND';
    }) => api.put('/dashboard/buitenland/oordeel', { costLineIds, oordeel }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['dashboard'] }),
  });
}
